import React, { useState, useEffect, useRef } from "react";
import { useSelector } from 'react-redux';

import { Typography, TextField, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert, IconButton, Tooltip } from "@mui/material";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import apiClient from "../networking/axiosconfig";

import { styled } from '@mui/material/styles';


const CompletedOrdersTable = ({ orders, selectedCategory, selectedStatus, onOrderUpdate, selectedStore }) => {
  const { data: config } = useSelector((state) => state.config);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState([]);
  const [uploadedFilter, setUploadedFilter] = useState("all"); // 'all', 'yes', 'no'
  const [districtFilter, setDistrictFilter] = useState(selectedCategory || "all");
  const [statusFilter, setStatusFilter] = useState(selectedStatus || "all");
  const contentRef = useRef(null);

  const handlePrint = async () => {
    if (!contentRef.current) return;
    if (!contentRef.current) return;
    const element = contentRef.current;
    // temporarily expand content
    const originalOverflow = element.style.overflow;
    const originalMaxHeight = element.style.maxHeight;
    element.style.overflow = 'visible';
    element.style.maxHeight = 'none';

    const canvas = await html2canvas(element, {
      useCORS: true,
      scale: 2,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      if (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
      }
    }

    // restore styles
    element.style.overflow = originalOverflow;
    element.style.maxHeight = originalMaxHeight;

    const pdfUrl = pdf.output('bloburl');
    const printWin = window.open(pdfUrl);
    if (printWin) {
      printWin.focus();
      // wait for pdf load then print
      printWin.onload = () => {
        printWin.print();
      };
    }
  };

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    const element = contentRef.current;
    // Ensure entire scrollable content is captured
    const originalOverflow = element.style.overflow;
    const originalMaxHeight = element.style.maxHeight;
    element.style.overflow = 'visible';
    element.style.maxHeight = 'none';
    // Render dialog exactly as seen using html2canvas
    const canvas = await html2canvas(element, {
      useCORS: true,
      scale: 2,
      backgroundColor: '#ffffff',
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      if (heightLeft > 0) {
        position = heightLeft - imgHeight; // reset for next page
        pdf.addPage();
      }
    }
    pdf.save(`order_${selectedOrder?._id || 'details'}.pdf`);
    // restore original styles
    element.style.overflow = originalOverflow;
    element.style.maxHeight = originalMaxHeight;
  };
  const [allDistricts, setAllDistricts] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [markCompleteLoading, setMarkCompleteLoading] = useState(false); // Loading state for mark as complete
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Snackbar state

  const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    flex: 1,
    overflow: "auto",
    maxHeight: "calc(100vh - 400px)",
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.grey[100],
    },
    '& .MuiTableCell-root': {
      border: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: '600',
      backgroundColor: theme.palette.grey[200],
    },
    [`& ${TableBody}-root ${TableRow}-root:nth-child(even)`]: {
      backgroundColor: 'red', // Light grey for even rows
    },
    [`& ${TableBody}-root ${TableRow}-root:nth-child(odd)`]: {
      backgroundColor:'red', // Default background for odd rows (usually white)
    },
  }));
  
  useEffect(() => {
    setLoading(false);
    // // Extract unique districts from inspectionData
    // const districts = [...new Set(inspectionData.map(item => item.District).filter(Boolean))];
    // setAllDistricts(["all", ...districts]);
    // Extract unique statuses from inspectionData
    const statuses = [...new Set(orders.map(item => item.status).filter(Boolean))];
    setAllStatuses(["all", ...statuses]);
    // if (selectedDistrict) {
    //   setDistrictFilter(selectedDistrict);
    // }

    if (selectedStatus) {
      setStatusFilter(selectedStatus);
    }

  }, [orders, selectedStatus]);




  const handleViewDetails = (inspection) => {
    setSelectedOrder(inspection);
    setOrderDetails(inspection);
    setUploadedFilter("all"); // Reset filter when modal opens
    setOpenDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setOpenDetailsModal(false);
    setSelectedOrder(null);
    setOrderDetails([]);
    setUploadedFilter("all"); // Reset filter when modal closes
  };

  const handleUploadedFilterChange = (event) => {
    setUploadedFilter(event.target.value);
  };

  const handleDistrictFilterChange = (event) => {
    setDistrictFilter(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };


  const handleDownloadExcel = () => {
    if (selectedOrder && orderDetails.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(orderDetails);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inspection Details');
      const filename = `${selectedOrder.vehicleNo}_details.xlsx`;
      XLSX.writeFile(workbook, filename);
    }
  };

  // const filteredorderDetails = orderDetails.filter(detail => {
  //   if (uploadedFilter === "all") return true;
  //   if (uploadedFilter === "yes") return detail.uploaded;
  //   if (uploadedFilter === "no") return !detail.uploaded;
  //   return true;
  // });

  // Function to mark inspection as complete
  const handleStatusUpdate = async (status) => {
    if (!selectedOrder) return;

    setMarkCompleteLoading(true);
    // Simulate an API call
    try {
      // Replace this with your actual API call
      await apiClient.put(`${config.API_BASE_URL}/api/orders/status`, { orderId: selectedOrder._id, status: status });

      // In a real application, you would use fetch or axios:
      // await fetch(`/api/inspections/${selectedOrder.id}/complete`, { method: 'POST' });

      // Simulate a successful update and close modal
      setSnackbar({ open: true, message: `Order marked as ${status}!`, severity: 'success' });
      handleCloseDetailsModal(); // Close the modal after completion
      // Update the status in the local data.  Important.
      setSelectedOrder(prev => ({ ...prev, status: status }));

      onOrderUpdate(); // Call the parent function to refresh the data
      await apiClient.post(`${config.API_BASE_URL}/notification/orderUpdate`, { 
        orderId: selectedOrder._id,
        title:"Order Status Update",
        body:"Your order is " + status + "." 
    });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update order status', severity: 'error' });
      console.error("Error marking order update:", error);
    } finally {
      setMarkCompleteLoading(false);
    }
  };
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Title */}


      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        {/* Search Box */}
        <Grid item xs={12} md={4}>
          <TextField
            label="Search by Order Id"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>

        {/* District Filter */}
        {/* <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="district-filter-label">Filter by Category</InputLabel>
            <Select
              labelId="district-filter-label"
              id="district-filter"
              value={districtFilter}
              onChange={handleDistrictFilterChange}
              label="Filter by District"
            >
              {allDistricts.map((district) => (
                <MenuItem key={district} value={district}>
                  {district === "all" ? "All" : district}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid> */}

        {/* Status Filter */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="status-filter-label">Filter by Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Filter by Status"
            >
              {allStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status === "all" ? "All" : status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && orders.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", marginTop: "20px" }}>
          No orders found matching your criteria.
        </Typography>
      ) : (
        <StyledTableContainer>
          <TableContainer
            component={Paper}
            sx={{ flex: 1, overflow: "auto", maxHeight: "calc(100vh - 400px)" }}
          >
            <Table size="small" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600'}}>S.No</TableCell>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600' }}>Order Id</TableCell>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600' }}>Address</TableCell>
                  <TableCell sx={{ fontSize: "14px" ,fontWeight:'600'}}>City</TableCell>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600' }}>Pincode</TableCell>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: "14px",fontWeight:'600' }}>No. of Products</TableCell>
                  <TableCell sx={{ fontSize: "14px" ,fontWeight:'600'}}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: row.status?.toLowerCase() === "completed" ? "success.main" : "inherit",
                      "&:hover": { backgroundColor: row.status?.toLowerCase() === "completed" ? "success.dark" : "#f5f5f5" },

                    }}
                  >
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row._id}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row.address?.addressLine1}{row.address?.addressLine2 ? `, ${row.address.addressLine2}` : ''}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row.address?.city || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row.address?.postalCode || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row.status || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit" }}>{row.products?.length || 0}</TableCell>
                    <TableCell sx={{ fontSize: "14px", color: row.status?.toLowerCase() === "completed" ? "white" : "inherit", }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewDetails(row)}
                        sx={{
                          color: row.status?.toLowerCase() === "completed" ? "white" : "inherit",
                          borderColor: row.status?.toLowerCase() === "completed" ? "white" : "inherit", // Added borderColor
                          "&:hover": { borderColor: row.status?.toLowerCase() === "completed" ? "warning.light" : "inherit" }
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledTableContainer>
      )}

      {/* Combined View/Missed Data Modal */}
      <Dialog open={openDetailsModal} onClose={handleCloseDetailsModal} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Order Details
          <Box>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPDF}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers ref={contentRef}>
          {selectedOrder && (
            <Grid container spacing={3}> {/* Increased spacing for better visual separation */}
              <Grid item xs={4}>
                <Typography variant="subtitle1" color="textSecondary">
                  Order ID:
                </Typography>
                <Typography variant="body1" fontWeight="semi-bold">
                  {selectedOrder._id}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="subtitle1" color="textSecondary">
                  Ordered Date:
                </Typography>
                <Typography variant="body1" fontWeight="semi-bold">
                  {new Date(selectedOrder.createdAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle1" color="textSecondary">
                  Status:
                </Typography>
                <Typography variant="body1" fontWeight="semi-bold">
                  {selectedOrder.status}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Typography variant="subtitle1" color="textSecondary">
                  Total Amount:
                </Typography>
                <Typography variant="body1" fontWeight="semi-bold">
                  ₹{selectedOrder.total}
                </Typography>
              </Grid>



              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Shipping Address:
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '2px solid #ccc', py: 1 }}> {/* Added visual indentation */}
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Name:</Box> {selectedOrder.address?.name}
                  </Typography>
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Type:</Box> {selectedOrder.address?.type}
                  </Typography>
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Address:</Box> {selectedOrder.address?.addressLine1}, {selectedOrder.address?.addressLine2}, {selectedOrder.address?.city} - {selectedOrder.address?.postalCode}
                  </Typography>
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Coordinates:</Box> {selectedOrder.address?.latitude}, {selectedOrder.address?.longitude}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Customer Details:
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '2px solid #ccc', py: 1 }}> {/* Added visual indentation */}
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Name:</Box> {selectedOrder.user?.name}
                  </Typography>
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Email:</Box> {selectedOrder.user?.email}
                  </Typography>
                  <Typography variant="body2">
                    <Box fontWeight="semi-bold" component="span">Phone:</Box> {selectedOrder.user?.phone || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Payment Information:
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 2,
                  pl: 2,
                  borderLeft: '2px solid #ccc',
                  py: 1
                }}>
                  <Box>
                    <Typography variant="body2">
                      <Box fontWeight="semi-bold" component="span">Payment Type:</Box> {selectedOrder.paymentType || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      <Box fontWeight="semi-bold" component="span">Payment Status:</Box> {selectedOrder.paymentStatus || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      <Box fontWeight="semi-bold" component="span">Payment ID:</Box> {selectedOrder.paymentId || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      <Box fontWeight="semi-bold" component="span">Amount Paid:</Box> ₹{selectedOrder.amount || selectedOrder.total || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Products:
                </Typography>
              </Grid>

              {selectedOrder.products?.map((item, idx) => (
                <Grid item xs={12} key={item._id}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      p: 2, // Increased padding
                      backgroundColor: '#f9f9f9', // Added a subtle background
                    }}
                  >
                    <img
                      src={item.product.images?.[0]}
                      alt={item.product.name}
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }} // Increased image size
                    />
                    <Box sx={{ flexGrow: 1 }}> {/* Allows product info to take remaining space */}
                      <Typography variant="h6" fontWeight="semi-bold" gutterBottom>
                        {item.product.name} ({item.product.brand})
                      </Typography>
                      <Typography variant="body2">
                        <Box fontWeight="semi-bold" component="span">Price:</Box> ₹{item.product.price}
                      </Typography>
                      <Typography variant="body2">
                        <Box fontWeight="semi-bold" component="span">Quantity:</Box> {item.quantity}
                      </Typography>
                      {item.product.highlights && (
                        <Typography variant="body2" color="textSecondary">
                          <Box fontWeight="semi-bold" component="span">Highlights:</Box> {item.product.highlights}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleStatusUpdate("Processing")}
            variant="outlined"
            disabled={selectedOrder?.status === "Processing"}
          >
            Mark as Processing
          </Button>
          <Button
            onClick={() => handleStatusUpdate("Completed")}
            variant="outlined"
            color="success"
            disabled={selectedOrder?.status === "Completed"}
          >
            Mark as Completed
          </Button>
          <Button onClick={handleCloseDetailsModal} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>


      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompletedOrdersTable;
